import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtTime } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { MessageCircle } from "lucide-react";

export default function WhatsAppCenter() {
  const qc = useQueryClient();
  const [to, setTo] = useState("");
  const [body, setBody] = useState("");
  const [promo, setPromo] = useState("");

  const { data: config } = useQuery({
    queryKey: ["whatsapp-config"],
    queryFn: async () => (await api.get("/whatsapp/config")).data,
  });

  const { data: logs = [] } = useQuery({
    queryKey: ["whatsapp-logs"],
    queryFn: async () => (await api.get("/whatsapp/logs")).data,
    refetchInterval: 8000,
  });

  const send = async () => {
    try {
      const { data } = await api.post("/whatsapp/send", { to, body });
      toast.success(data.status === "sent" ? "WhatsApp sent" : `Logged (${data.status}). Configure Twilio WhatsApp to send live.`);
      setBody("");
      qc.invalidateQueries({ queryKey: ["whatsapp-logs"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const blast = async () => {
    try {
      const { data } = await api.post("/whatsapp/broadcast", { body: promo });
      toast.success(`Queued to ${data.sent} of ${data.total} customers`);
      setPromo("");
      qc.invalidateQueries({ queryKey: ["whatsapp-logs"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8" data-testid="whatsapp-center">
      <PageHeader title="WhatsApp Center" subtitle="Order alerts, menu broadcasts, and CRM promos">
        <div className="flex flex-wrap gap-1.5">
          <Badge className={config?.transport ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-800"}>
            Transport {config?.transport ? "ready" : "not configured"}
          </Badge>
          <Badge className={config?.enabled !== false ? "bg-green-100 text-green-700" : "bg-neutral-100 text-neutral-500"}>
            {config?.enabled !== false ? "Enabled" : "Disabled"}
          </Badge>
          <Badge className={config?.auto !== false ? "bg-blue-50 text-blue-700" : "bg-neutral-100 text-neutral-500"}>
            Auto {config?.auto !== false ? "on" : "off"}
          </Badge>
          {config?.owner && <Badge className="bg-neutral-100 text-neutral-600">Owner number set</Badge>}
        </div>
      </PageHeader>

      <div className="grid lg:grid-cols-2 gap-4 max-w-5xl">
        <div className="kul-card p-5 space-y-3">
          <h3 className="font-display font-700 flex items-center gap-2">
            <MessageCircle className="w-4 h-4" /> Send WhatsApp
          </h3>
          <div>
            <Label>Phone</Label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="+91…" />
          </div>
          <div>
            <Label>Message</Label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} className="w-full rounded-lg border p-2 text-sm" />
          </div>
          <Button onClick={send} className="bg-[var(--kul-primary)] text-white">Send</Button>
          <div className="border-t pt-3">
            <Label>Broadcast to all CRM phones</Label>
            <textarea
              value={promo}
              onChange={(e) => setPromo(e.target.value)}
              rows={2}
              className="w-full rounded-lg border p-2 text-sm mt-1"
              placeholder="Weekend thali special…"
            />
            <Button variant="outline" className="mt-2" onClick={blast}>Broadcast</Button>
          </div>
        </div>

        <div className="kul-card p-5">
          <h3 className="font-display font-700 mb-3">Outbox</h3>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {logs.map((l) => (
              <div key={l.id} className="text-sm border-b pb-2">
                <div className="flex justify-between gap-2">
                  <span className="font-medium truncate">{l.to || "—"}</span>
                  <Badge className="capitalize shrink-0">{l.status}</Badge>
                </div>
                <div className="text-neutral-600 mt-1 whitespace-pre-wrap">{l.body}</div>
                <div className="text-[11px] text-neutral-400">{l.kind} · {fmtTime(l.created_at)}</div>
              </div>
            ))}
            {!logs.length && (
              <p className="text-neutral-400">No messages yet. Order events auto-notify guests when WhatsApp is configured.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
